import { Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      backgroundColor: theme.palette.grey[400],
      boxSizing: "border-box",
      fontFamily: theme.typography.fontFamily,
    },

    card: {
      width: "100%",
      backgroundColor: theme.palette.background.paper,
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      padding: "24px 24px 18px",
      boxSizing: "border-box",
    },

    sectionHeading: {
      color: `${theme.palette.secondary.main} !important`,
      fontSize: "15px !important",
      fontWeight: "500 !important",
      marginBottom: "14px !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      textAlign: "right",
    },

    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: 10,
      marginBottom: 22,
      direction: "rtl",
    },

    summaryCard: {
      minHeight: 52,
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      padding: "8px 10px",
      boxSizing: "border-box",
      textAlign: "right",
      minWidth: 0,
    },

    summaryLabel: {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "12px !important",
      fontWeight: "500 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    summaryValue: {
      marginTop: "4px !important",
      color: `${theme.palette.success.main} !important`,
      fontSize: "13px !important",
      fontWeight: "500 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
    },

    divider: {
      width: "100%",
      height: 1,
      backgroundColor: theme.palette.divider,
      margin: "18px 0",
    },

    detailsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: 50,
      padding: "4px 10px",
      direction: "rtl",
    },

    infoColumn: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      minWidth: 0,
    },

    infoRow: {
      display: "grid",
      gridTemplateColumns: "120px 1fr",
      gap: 12,
      alignItems: "center",
      minWidth: 0,
    },

    infoLabel: {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "13px !important",
      fontWeight: "500 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      whiteSpace: "nowrap",
      textAlign: "right",
    },

    infoValue: {
      color: `${theme.palette.secondary.main} !important`,
      fontSize: "13px !important",
      fontWeight: "400 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      whiteSpace: "nowrap",
      textAlign: "right",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    radioRow: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 8,
      marginBottom: 14,
      direction: "rtl",
    },

    sectionTitle: {
      color: `${theme.palette.secondary.main} !important`,
      fontSize: "13px !important",
      fontWeight: "500 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      textAlign: "right",
      whiteSpace: "nowrap",
    },

    radioGroup: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 8,
      direction: "rtl",
    },

    optionLabel: {
      height: 38,
      width: "100%",
      margin: "0 !important",
      padding: "0 12px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      boxSizing: "border-box",
      color: theme.palette.text.primary,
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      direction: "rtl",

      "& .MuiRadio-root": {
        padding: "0 0 0 8px !important",
        color: `${theme.palette.text.primary} !important`,
      },

      "& .MuiRadio-root.Mui-checked": {
        color: `${theme.palette.primary.main} !important`,
      },

      "& .MuiFormControlLabel-label": {
        fontSize: 13,
        fontWeight: 400,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.secondary.main,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },

    subSectionTitle: {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "13px !important",
      fontWeight: "500 !important",
      margin: "10px 0 8px !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      textAlign: "right",
    },

    checkGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px 14px",
      direction: "rtl",
    },

    checkItem: {
      height: 38,
      display: "flex",
      alignItems: "center",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      padding: "0 10px",
      boxSizing: "border-box",
      backgroundColor: theme.palette.background.paper,
      direction: "rtl",
      minWidth: 0,
    },

    checkLabel: {
      width: "100%",
      margin: "0 !important",
      color: theme.palette.secondary.main,
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      direction: "rtl",

      "& .MuiCheckbox-root": {
        padding: "0 0 0 8px !important",
        color: `${theme.palette.text.primary} !important`,
      },

      "& .MuiCheckbox-root.Mui-checked": {
        color: `${theme.palette.primary.main} !important`,
      },

      "& .MuiFormControlLabel-label": {
        fontSize: 13,
        fontWeight: 400,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.secondary.main,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },

    quantityGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(8, minmax(120px, 1fr))",
      gap: 12,
      direction: "rtl",
    },

    quantityItem: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      minWidth: 0,
      direction: "rtl",
    },

    quantityLabel: {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "12px !important",
      fontWeight: "400 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "right",
    },

    quantityBox: {
      height: 48,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 10px",
      boxSizing: "border-box",
      color: theme.palette.secondary.main,
      fontSize: 13,
      fontWeight: 400,
      backgroundColor: theme.palette.background.paper,
      direction: "ltr",
    },

    quantityArrows: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0,
      color: theme.palette.text.primary,
    },

    quantityArrowIcon: {
      fontSize: "16px !important",
      color: theme.palette.text.primary,
      lineHeight: 1,
      height: 12,
    },

    quantityValue: {
      color: `${theme.palette.secondary.main} !important`,
      fontSize: "13px !important",
      fontWeight: "400 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
      minWidth: 20,
      textAlign: "center",
    },

    loadingWrapper: {
      minHeight: 420,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    emptyText: {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "15px !important",
      fontWeight: "400 !important",
      fontFamily: `${theme.typography.fontFamily} !important`,
    },

    "@media (max-width: 1400px)": {
      summaryGrid: {
        gridTemplateColumns: "repeat(6, minmax(90px, 1fr))",
      },

      quantityGrid: {
        gridTemplateColumns: "repeat(4, minmax(120px, 1fr))",
        direction: "rtl",
      },
    },

    "@media (max-width: 1100px)": {
      detailsGrid: {
        gridTemplateColumns: "1fr",
      },

      checkGrid: {
        gridTemplateColumns: "1fr",
      },

      radioGroup: {
        gridTemplateColumns: "1fr",
      },

      radioRow: {
        gridTemplateColumns: "1fr",
      },

      quantityGrid: {
        gridTemplateColumns: "repeat(2, minmax(120px, 1fr))",
        direction: "rtl",
      },
    },
  })
);
